import { Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type SiteDataTone = 'severe' | 'hint' | null;

/**
 * The ONE rule for "this site has a data problem". Exported so an aggregate (the chain head chip)
 * counts exactly what the badge renders, instead of a second copy that drifts.
 */
export function siteDataTone(site: {
  producer: boolean;
  hasGeoJson?: boolean;
  hasSpecies?: boolean;
  codeCount?: number;
  severe?: boolean;
}): SiteDataTone {
  if (!site.producer) return null;
  if (site.severe) return 'severe';
  if (site.hasGeoJson === false || site.hasSpecies === false || (site.codeCount ?? 0) > 0) return 'hint';
  return null;
}

/** Distinct finding codes out of the opaque JSON the server stores, or an already-extracted list. */
export function siteDataCodes(raw: string | string[] | null | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return [...new Set(raw.filter((c) => !!c))];
  try {
    const parsed: unknown = JSON.parse(raw);
    const list: unknown[] = Array.isArray(parsed) ? parsed : ((parsed as { findings?: unknown[] })?.findings ?? []);
    const codes = list
      .map((f) => (typeof f === 'string' ? f : (f as { code?: string })?.code))
      .filter((c): c is string => !!c);
    return [...new Set(codes)];
  } catch {
    return [];
  }
}

/** A report archive's tier as the server freezes it. */
export interface FrozenTierSiteData {
  species?: string[];
  speciesKnown?: boolean;
  geoCheckFindings?: string[];
  geoCheckSevere?: boolean;
}

/**
 * Folds the tiers FROZEN with a report archive into the three badge inputs. Exported so the
 * archive list, the archive detail and the DDS report card all fold them the same way instead of
 * carrying three copies that drift.
 *
 * `hasSpecies` stays `undefined` unless at least one tier actually froze a species list (T9): a
 * report written before species capture knows nothing, and must not be given a verdict after the
 * fact.
 */
export function reportArchiveSiteData(degrees: FrozenTierSiteData[] | undefined | null): {
  findings: string[];
  severe: boolean;
  hasSpecies: boolean | undefined;
} {
  const tiers = degrees ?? [];
  const known = tiers.filter((d) => d.speciesKnown);
  return {
    findings: [...new Set(tiers.flatMap((d) => d.geoCheckFindings ?? []))],
    severe: tiers.some((d) => !!d.geoCheckSevere),
    hasSpecies: known.length ? !known.some((d) => (d.species?.length ?? 0) === 0) : undefined,
  };
}

/**
 * One statement per producer site: its geodata are missing, incomplete or topologically broken.
 *
 * Severity is never re-derived here — `severe` arrives decided by the server
 * (`GeoFindingCodes.isSevere`). Red means objectively wrong geometry and holds regardless of what
 * a legal norm demands; amber means incomplete, and whether that counts is the evaluation's call.
 *
 * `hasGeoJson` / `hasSpecies` claim "missing" only on an explicit `false`: a field the caller
 * could not determine (hidden by the degree filter, absent from an older payload) must never be
 * reported as a gap.
 */
@Component({
  selector: 'app-site-data-badge',
  standalone: true,
  imports: [MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    @if (tone(); as t) {
      <span
        class="site-data-badge"
        [class.site-data-badge--severe]="t === 'severe'"
        [class.site-data-badge--hint]="t === 'hint'"
        [attr.data-test]="t === 'severe' ? 'site-data-badge-severe' : 'site-data-badge-hint'"
        [matTooltip]="tooltip()"
        matTooltipClass="multiline-tooltip"
      >
        <mat-icon>{{ t === 'severe' ? 'report_problem' : 'info' }}</mat-icon>
        {{ (t === 'severe' ? 'site_data_badge_severe_tr' : 'site_data_badge_incomplete_tr') | translate }}
      </span>
    }
  `,
  styles: [
    `
      .site-data-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.75em;
        line-height: 1;
        padding: 3px 8px;
        border-radius: 12px;
        white-space: nowrap;
      }
      .site-data-badge mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
      }
      .site-data-badge--severe {
        color: #b3261e;
        background: rgba(179, 38, 30, 0.1);
      }
      .site-data-badge--hint {
        color: #6b5b00;
        background: rgba(107, 91, 0, 0.1);
      }
    `,
  ],
})
export class SiteDataBadgeComponent {
  private readonly translate = inject(TranslateService);

  /** Only producer sites carry geodata duties — anything else renders nothing. */
  readonly producer = input<boolean>(false);
  /** `false` = the site has no geometry. `undefined` = not determinable, never a gap. */
  readonly hasGeoJson = input<boolean | undefined>(undefined);
  /** `false` = the site has no tree species. `undefined` = not determinable, never a gap. */
  readonly hasSpecies = input<boolean | undefined>(undefined);
  /** Raw findings as stored on the site (JSON string) or the codes already extracted. */
  readonly findings = input<string | string[] | null | undefined>(undefined);
  /** Decided by the server. Never re-derived here. */
  readonly severe = input<boolean>(false);

  readonly codes = computed(() => siteDataCodes(this.findings()));

  readonly tone = computed<SiteDataTone>(() =>
    siteDataTone({
      producer: this.producer(),
      hasGeoJson: this.hasGeoJson(),
      hasSpecies: this.hasSpecies(),
      codeCount: this.codes().length,
      severe: this.severe(),
    }),
  );

  readonly tooltip = computed(() => {
    const lines: string[] = [];
    if (this.hasGeoJson() === false) lines.push(this.translate.instant('site_data_missing_geo_tr'));
    if (this.hasSpecies() === false) lines.push(this.translate.instant('site_data_missing_species_tr'));
    for (const code of this.codes()) {
      const key = `geo_check_code_${code}_tr`;
      const text = this.translate.instant(key);
      lines.push(text === key ? code : text);
    }
    return lines.join('\n');
  });
}
